import { TaskModel } from '@bryntum/gantt';
import moment, { MomentInput } from 'moment';
import { DEFAULT_DATE_FORMAT } from 'api/models';
import { ProjectBaseline, ProjectPhase } from 'api/project';
import { TaskType } from 'api/tasks';
import { DependenciesData } from 'components/atoms/gantt';
import { GanttBarColors } from 'components/colors';
import { Task } from 'data/tasks';
import { DateShiftUpdates, EntityType, List } from 'utils/date-shift';
import {
  BaselineList,
  GanttAssignee,
  GanttPhase,
  GanttTask,
  PhaseList,
  TaskList,
} from './types';

export enum GanttType {
  ProjectPhaseBar = 'PROJECT_PHASE',
  TaskBar = 'TASK',
  MilestoneBar = 'MILESTONE',
}

export const GANTT_TOOLTIP_DATE_FORMAT = 'MMM DD, YYYY';

export const getGanttPhaseId = (id: number): string =>
  `${GanttType.ProjectPhaseBar}-${id}`;

export const getGanttTaskId = (id: number): string =>
  `${GanttType.TaskBar}-${id}`;

export const formatDate = (date: MomentInput, format?: string): MomentInput =>
  moment(date).format(format ?? DEFAULT_DATE_FORMAT);

/**
 * Bryntum doesn't include the end/ due date for plotting gantt bars
 * hence to make the due date inclusive, it is incremented by 1
 * @param dueDate
 * @returns
 */
export const getDueDateForGantt = (dueDate: MomentInput): MomentInput =>
  formatDate(moment(dueDate).add(1, 'day'));

/**
 * Since due date is incremented by 1 and passed to the gantt for the reason in the API `getDueDateForGantt`
 * to retrieve the actual due date from task record in gantt store
 * it is decremented by 1.
 * @param entity
 * @returns
 */
export const getDueDateFromGantt = (
  entity: TaskModel,
  format?: string,
): MomentInput => formatDate(moment(entity.endDate).subtract(1, 'day'), format);

export const getDependenciesData = (
  phaseList: PhaseList,
  taskList: TaskList,
): DependenciesData[] => {
  const dependenciesData: DependenciesData[] = [];

  Object.values(phaseList).forEach((phase) => {
    phase.dependencies?.forEach((dependencyId) => {
      if (phaseList[dependencyId]) {
        dependenciesData.push({
          id: `${phase.id}-${dependencyId}`,
          fromEvent: `${GanttType.ProjectPhaseBar}-${dependencyId}`,
          toEvent: `${GanttType.ProjectPhaseBar}-${phase.id}`,
        });
      }
    });
  });

  Object.values(taskList).forEach((task) => {
    task.dependencies?.forEach((dependencyId) => {
      if (taskList[dependencyId]) {
        dependenciesData.push({
          id: `${task.id}-${dependencyId}`,
          fromEvent: `${GanttType.TaskBar}-${dependencyId}`,
          toEvent: `${GanttType.TaskBar}-${task.id}`,
        });
      }
    });
  });

  return dependenciesData;
};

export const getPhaseList = (phases: ProjectPhase[]): List => {
  const result: List = {};

  phases.forEach(
    ({
      projectPhaseId,
      projectPhaseName,
      startDate,
      dueDate,
      taskOrder,
      tasks,
      dependentProjectPhases,
    }) => {
      result[projectPhaseId] = {
        depth: 0,
        id: projectPhaseId,
        name: projectPhaseName,
        type: EntityType.Phase,
        startDate,
        dueDate,
        children: taskOrder ?? tasks.map((_task) => _task.taskId ?? _task),
        dependentOn: dependentProjectPhases?.map(
          ({ projectPhaseId: dPhaseId }) => dPhaseId,
        ),
      };

      dependentProjectPhases?.forEach(({ projectPhaseId: dPhaseId }) => {
        result[dPhaseId] = {
          ...(result[dPhaseId] ?? {}),
          dependencyFor: [
            ...(result[dPhaseId]?.dependencyFor ?? []),
            projectPhaseId,
          ],
        };
      });
    },
  );

  return result;
};

export const getTaskList = (tasks: Task[], phaseList: List): List => {
  const result: List = {};

  tasks.forEach(
    ({
      taskId,
      startDate,
      dueDate,
      subTasks,
      dependentTasks,
      parentTask,
      projectPhase,
      taskName,
    }) => {
      result[taskId] = {
        ...(result[taskId] ?? {}),
        depth: 1, // initial value
        name: taskName,
        id: taskId,
        type: EntityType.Task,
        hasStartDate: !!startDate,
        hasDueDate: !!dueDate,
        startDate,
        dueDate,
        children: subTasks?.map((_task) => _task),
        dependentOn: dependentTasks?.map(({ taskId: dTaskId }) => dTaskId),
        parent: parentTask ?? projectPhase?.projectPhaseId,
      };

      dependentTasks?.forEach(({ taskId: dTaskId }) => {
        result[dTaskId] = {
          ...(result[dTaskId] ?? {}),
          dependencyFor: [...(result[dTaskId]?.dependencyFor ?? []), taskId],
        };
      });
    },
  );

  const getDepth = (id) => {
    return !result[id]?.parent ? 0 : getDepth(result[id].parent) + 1;
  };

  const getStartDate = (id: number): MomentInput => {
    const startDate = (result[id] || phaseList[id])?.startDate;
    const parent = (result[id] || phaseList[id])?.parent;

    return startDate || (parent && getStartDate(parent));
  };

  Object.values(result).forEach((_task) => {
    result[_task.id] = {
      ..._task,
      depth: getDepth(_task.id),
    };
  });

  /**
   * Pick parent's start date for empty start date.
   */
  Object.values(result)
    .filter((_task) => !_task.startDate)
    .forEach((_task) => {
      result[_task.id] = {
        ..._task,
        startDate: _task.dueDate || getStartDate(_task.id),
      };
    });

  /**
   * Assign due date same as start date for empty due date.
   */
  Object.values(result)
    .filter((_task) => !_task.dueDate)
    .forEach((_task) => {
      result[_task.id] = {
        ..._task,
        dueDate: _task.startDate,
      };
    });

  return result;
};

export const getGanttPhaseList = (
  rawPhases: ProjectPhase[],
  baselineList: BaselineList['phases'],
) => {
  const tPhases = {} as PhaseList;

  rawPhases.forEach(
    ({
      projectPhaseId,
      projectPhaseName,
      startDate,
      dueDate,
      project,
      fields,
      dependentProjectPhases,
      ...rest
    }) => {
      tPhases[projectPhaseId] = {
        id: projectPhaseId,
        name: projectPhaseName,
        projectId: project.projectId,
        startDate,
        dueDate,
        fields,
        type: 'phase',
        private: rest.private,
        children: rest.taskOrder ?? [],
        baselines: baselineList?.[projectPhaseId],
        dependencies:
          dependentProjectPhases?.map(
            ({ projectPhaseId: _phaseId }) => _phaseId,
          ) ?? [],
        // status: _phase.progressStatus,
      } as unknown as GanttPhase;
    },
  );

  return tPhases;
};

export const filterTaskUpdates = (
  taskUpdates: DateShiftUpdates,
  _tasks: Task[],
): DateShiftUpdates => {
  const filteredUpdates: DateShiftUpdates = {};

  Object.values(taskUpdates).forEach((update) => {
    const { id } = update;
    const _update = { ...update };
    const task = _tasks.find((_task) => _task.taskId === id);

    if (!task?.startDate && _update.startDate) {
      delete _update.startDate;
    }

    if (!task?.dueDate && _update.dueDate) {
      delete _update.dueDate;
    }

    filteredUpdates[id] = { ..._update };
  });

  return filteredUpdates;
};

export const getParentStartDate = (id: number, lookup: List): MomentInput => {
  const { startDate, parent } = lookup[id];

  return startDate || (parent && getParentStartDate(parent, lookup));
};

export const convertToGanttTask = ({
  taskId,
  taskName,
  startDate,
  dueDate,
  projectPhase,
  parentTask,
  subTasks,
  project,
  dependentTasks,
  completedAt,
  responsible,
  progress,
  assignee,
  type,
  fields,
  status,
  formInstanceId,
  ...rest
}: Task): GanttTask => {
  return {
    id: taskId,
    name: taskName,
    projectId: project,
    startDate,
    dueDate,
    type: type ?? TaskType.Task,
    parent: parentTask ?? projectPhase?.projectPhaseId,
    children: subTasks ?? [],
    private: rest.private,
    completedAt,
    responsible,
    progress,
    fields,
    dependencies: dependentTasks?.map(({ taskId: _taskId }) => _taskId) ?? [],
    assignees:
      assignee?.users?.map(
        ({ userId, firstName, lastName }) =>
          ({
            id: userId,
            firstName,
            lastName,
          } as GanttAssignee),
      ) ?? [],
    status,
    formInstanceId,
  } as GanttTask;
};

export const getGanttTaskList = (
  rawTasks: Task[],
  baselineList: BaselineList['tasks'],
): TaskList => {
  const tTasks = {} as TaskList;

  rawTasks.forEach((task) => {
    tTasks[task.taskId] = {
      ...convertToGanttTask(task),
      baselines: baselineList?.[task.taskId],
    };
  });

  return tTasks;
};

export const convertToGanttStoreFormat = (
  item: GanttTask,
  parentStartDate: MomentInput,
  rootIndex: number,
  currentLevel: number,
) => {
  let startDate: MomentInput;
  let endDate: MomentInput;

  if (!item.dueDate) {
    startDate = formatDate(item.startDate || parentStartDate);
    endDate = getDueDateForGantt(startDate);
  } else if (!item.startDate) {
    endDate = getDueDateForGantt(item.dueDate);
    startDate = formatDate(item.dueDate);
  } else {
    startDate = formatDate(item.startDate);
    // endDate = moment(item.dueDate).format(DEFAULT_DATE_FORMAT);
    endDate = getDueDateForGantt(item.dueDate);
  }
  /**
   * Gantt excludes end date by default; hence to include end date, we are
   * 1. marking due date as end of day of the set due date explicitly
   * 2. avoid passing duration
   */
  // const duration = moment(endDate).diff(moment(startDate), 'days');

  return {
    id: `${GanttType.TaskBar}-${item.id}`,
    expanded: true,
    manuallyScheduled: true,
    title: item.name,
    name: item.name,
    cls: `gantt-task ${item.private ? 'gantt-private-row' : ''} gantt-bg-${
      GanttBarColors[rootIndex % GanttBarColors.length]
    } gantt-level-${currentLevel}`,
    startDate,
    endDate,
    // duration: item.type === `${GanttType.MilestoneBar}` ? 0 : duration || 1,

    type: GanttType.TaskBar,
    data: { ...item, rootIndex, currentLevel },
    percentDone: item.progress,
    isCalculatedStartDate: !item.startDate,
    isCalculatedEndDate: !item.dueDate,
    baselines: item.baselines,
  };
};

const mapTasks = (
  taskIds: number[],
  taskList: TaskList,
  parentStartDate: MomentInput,
  rootIndex: number,
  parentLevel: number,
) => {
  const currentLevel = parentLevel + 1;

  return taskIds
    .map((itemId) => {
      const item = taskList[itemId];

      if (!item) {
        return null;
      }
      const formattedTask = convertToGanttStoreFormat(
        item,
        parentStartDate,
        rootIndex,
        currentLevel,
      );

      return {
        ...formattedTask,
        children: mapTasks(
          item.children,
          taskList,
          formattedTask.startDate,
          rootIndex,
          currentLevel,
        ),
      };
    })
    .filter(Boolean);
};

export const mapPhases = (
  phaseOrder: number[],
  phases: PhaseList,
  tasks: TaskList,
) => {
  const orderedPhases = phaseOrder.map((phaseId) => phases[phaseId]);

  return Object.entries(orderedPhases).map(([, phase], index) => {
    const startDate = formatDate(phase.startDate);

    // const endDate = moment(phase.dueDate).format(DEFAULT_DATE_FORMAT);
    const endDate = getDueDateForGantt(phase.dueDate);

    return {
      id: getGanttPhaseId(phase.id),
      expanded: true,
      manuallyScheduled: true,
      resizable: false,
      title: phase.name,
      name: phase.name,
      cls: `gantt-project_phase ${
        phase.private ? 'gantt-private-row' : ''
      } gantt-bg-${
        GanttBarColors[index % GanttBarColors.length]
      }  level-${index}`,
      startDate,
      endDate,
      // duration: moment(phase.dueDate).diff(moment(phase.startDate), 'days'),
      children: phase.children
        ? mapTasks(phase.children, tasks, startDate, index, 0)
        : [],
      type: GanttType.ProjectPhaseBar,
      data: phase,
      baselines: phase.baselines,
    };
  });
};

export const getBaselineList = (
  baselines: ProjectBaseline[],
  phaseIds: number[],
  taskIds: number[],
): BaselineList => {
  const list = {} as BaselineList;

  list.phases = {};
  list.tasks = {};

  baselines?.forEach(({ projectPhases, tasks, baselineName, baselineId }) => {
    const phaseMap = projectPhases.reduce(
      (prev, _phase) => ({
        ...prev,
        [_phase.projectPhaseId]: _phase,
      }),
      {},
    );

    const taskMap = tasks.reduce(
      (prev, _task) => ({
        ...prev,
        [_task.taskId]: _task,
      }),
      {},
    );

    phaseIds?.forEach((_id) => {
      const baseline = phaseMap[_id];

      list.phases[_id] = [
        ...(list.phases[_id] || []),
        {
          startDate: baseline
            ? moment(baseline.startDate).format('YYYY-MM-DD')
            : null,
          endDate: baseline
            ? moment(getDueDateForGantt(baseline.dueDate)).format('YYYY-MM-DD')
            : null,
          name: baselineName,
          id: baselineId,
        },
      ];
    });
    taskIds?.forEach((_id) => {
      const baseline = taskMap[_id];

      list.tasks[_id] = [
        ...(list.tasks[_id] || []),
        {
          startDate: baseline
            ? moment(baseline.startDate).format('YYYY-MM-DD')
            : null,
          endDate: baseline
            ? moment(getDueDateForGantt(baseline.dueDate)).format('YYYY-MM-DD')
            : null,
          name: baselineName,
          id: baselineId,
        },
      ];
    });
  });

  return list;
};
